/**
 * Academy administrator guide (AUTONOMY · Layer 3).
 * Target role: owner / org_admin (and campus_admin for their campus).
 */
import { msg } from "@lingui/core/macro";
import { Building2 } from "@fleet/icons";

import { canCreateOrganization } from "@space/shared";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, P, Steps } from "../components/help-prose";

export const adminGuide: HelpGuide = {
  key: "help.admin",
  slug: "administrador",
  title: msg`Academy administrator guide`,
  description: msg`Set up your academy end to end: campuses, settings, people, academic structure and feature toggles — no developer needed.`,
  audience: msg`Owners and academy administrators`,
  icon: Building2,
  sections: [
    // §L1 · online-first posture: the section that explains the switch and,
    // above all, that turning it off does NOT delete anything.
    {
      id: "modalidad",
      title: msg`Online or in-person`,
      summary: msg`This academy runs online only. Everything in-person — campuses, rooms, QR roll-call and the "in person" and "hybrid" cohort modalities — is built and tested, but switched off: it stays out of sight until you turn the "In-person modality" feature on.`,
      body: () => (
        <>
          <P
            msg={msg`New cohorts are created as "live online" by default, which is what the academy sells. Turning the feature on does not create anything new: it brings back screens and options that already exist.`}
          />
          <Bullets
            items={[
              msg`Off: no campuses or rooms screen, no QR roll-call, and cohorts can only be online or self-paced.`,
              msg`Your data is never deleted: campuses, rooms and past attendance stay exactly as they are, and reappear when you switch it back on.`,
              msg`Cohorts already created in person or hybrid keep their modality — nothing is rewritten behind your back.`,
            ]}
          />
          <GoTo to="/admin/features" label={msg`Open the feature catalog`} />
        </>
      ),
    },
    {
      id: "estructura",
      title: msg`Organization, campuses and rooms`,
      summary: msg`Your academy is an organization. Inside it you create campuses (physical branches) and rooms, which schedules and cohorts use later. This only applies when the in-person modality is on.`,
      body: () => (
        <>
          <Steps
            items={[
              // Tenancy mode: "(or create one)" is FALSE in "single academy"
              // mode — creation is denied by the resolution, so the guide
              // would be sending the admin to a 403. Two variants are chosen
              // WRITTEN OUT IN FULL, not an interpolation: it's the same
              // doctrine as the vocabulary registry (a split sentence can't
              // be translated or reordered).
              canCreateOrganization()
                ? msg`Open Organizations and select your academy (or create one).`
                : msg`Open Organizations and select your academy.`,
              msg`In the Campuses tab, add each branch with the details you need to run it.`,
              msg`Inside a campus, add its rooms with capacity — the schedule uses them to detect room conflicts.`,
            ]}
          />
          <Note
            msg={msg`Campus admins only see and manage their own campus; owners and org admins see every campus.`}
          />
          <GoTo to="/organizations" label={msg`Open Organizations`} />
        </>
      ),
    },
    {
      id: "configuracion",
      title: msg`Academy settings`,
      summary: msg`The Settings tab of your organization controls country pack, timezone, currencies, grading scale, absence alerts and branding for the whole academy.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Country pack: sets local conventions — ID document label (cédula), tax id (RIF), phone prefix and date format.`,
              msg`Currencies: a primary currency (USD) and a secondary one (VES). Amounts are shown in both using the exchange rate of the day.`,
              msg`Grading scale: the default scale for new courses and cohorts (Venezuela 0–20, percentage, letters or pass/fail). Courses and cohorts can override it.`,
              msg`Absence alert threshold: consecutive absences that open a risk alert for a student.`,
              msg`Branding: public academy name and receipt footer used on printable receipts.`,
            ]}
          />
          <Note
            msg={msg`The exchange rate itself is managed in Finance → Rates, so every change is kept in the daily history.`}
          />
          <GoTo to="/organizations" label={msg`Open your organization settings`} />
        </>
      ),
    },
    {
      id: "grading-models",
      title: msg`Grading models`,
      summary: msg`A grading model is a reusable scale: its maximum value, how a grade is displayed, and the mark needed to pass (e.g. "passes with 10 / 20"). Manage them from the Grading models tab of your organization.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Every academy starts with 4 models: Venezuela 0–20, percentage, letters A–F and pass/fail. They are a starting point, not a rule — edit, delete or duplicate them like any model you create yourself.`,
              msg`Duplicate a model to create a variant — for example an English course that passes with 12 / 20 instead of 10.`,
              msg`Each model sets its maximum value, decimals, display suffix and a configurable passing mark (shown in native units). Letter models also define bands.`,
              msg`Courses and cohorts pick a model; a cohort overrides the course, which overrides the academy default. Already-sealed grades never change retroactively.`,
              msg`Once a model has grades it is locked: it can no longer be edited or deleted, so no change can move who passed after the fact. To grade differently, duplicate it and point the course at the copy — the old grades keep their scale.`,
              msg`A model a course or cohort has selected cannot be deleted until it is no longer selected, even if nobody has graded with it yet.`,
            ]}
          />
          <GoTo to="/organizations" label={msg`Open Grading models`} />
        </>
      ),
    },
    {
      id: "aprobacion",
      title: msg`Completion rules`,
      summary: msg`Completion rules decide who passes a cohort — and therefore who receives a certificate when it closes. Combine criteria with AND / OR from the course or cohort settings.`,
      body: () => (
        <>
          <P
            msg={msg`Without rules, a cohort keeps the classic behavior: whoever reaches the passing grade is certified. Turn on the "Reglas de aprobación" feature to require more than a grade.`}
          />
          <Bullets
            items={[
              msg`Minimum grade: the weighted final grade must reach a threshold (defaults to the grading model's passing mark).`,
              msg`Minimum attendance: a percentage of sessions attended (present or late).`,
              msg`Challenges passed: a number of the cohort's assignments (excluding the final project) graded above the pass mark.`,
              msg`Final project: the project-type assignment must be graded above the pass mark.`,
              msg`Instructor mark: the teacher marks a student complete by hand.`,
            ]}
          />
          <Steps
            items={[
              msg`Combine criteria with "all of" (every one required) and "any of" (one is enough) — for example: passing grade OR (75% attendance AND final project).`,
              msg`Set rules on a course to apply them to all its cohorts, or on a single cohort to override that course.`,
              msg`Students see their live progress on their academic profile; teachers see the whole roster in the cohort's completion report.`,
              msg`On cohort close, only students who meet the rules are completed and certified.`,
            ]}
          />
          <Note
            msg={msg`Turning the feature off never deletes anything: existing rules and certificates are preserved; the cohort simply falls back to the grade gate for new closes.`}
          />
          <GoTo to="/academy" label={msg`Open Academy`} />
        </>
      ),
    },
    {
      id: "personas",
      title: msg`People, roles and invitations`,
      summary: msg`Every person in the academy is a member with a role: owner, org admin, campus admin, teacher, staff, student or guardian. Roles decide what each screen allows.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Invite people from your organization page with their role and campus already assigned.`,
              msg`Change an existing member's role or campus from the organization's Members tab.`,
              msg`Deactivate a member to suspend access without deleting their history.`,
              msg`Link guardians to students from the student's record — guardians follow their children's progress and debt.`,
            ]}
          />
          <Note
            title={msg`Safety`}
            msg={msg`You cannot demote or remove the last owner, and only owners and org admins can manage roles.`}
          />
          <Note
            title={msg`Who can see email addresses`}
            msg={msg`Everyone in the academy sees the name and picture of the other members, but the email address is only served to whoever can manage members or read campus data: owners, org admins, campus admins, staff and teachers. Students and guardians never receive their classmates' or the staff's email — they only see their own.`}
          />
          <GoTo to="/academy/people" label={msg`Open People directory`} />
        </>
      ),
    },
    // P41 · the two guardian-consent features. Documents that they start
    // OFF and why enabling the teeth without dates of birth on file silences
    // the student body: it's a decision an academy has to make with the
    // data in front of it.
    {
      id: "consentimiento",
      title: msg`Guardian consent`,
      summary: msg`Record, prove and withdraw the authorization a minor's guardian gives, and optionally require it before the student can post.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Link the guardian to the student on the student's record. A link is not an authorization: it starts as pending.`,
              msg`Ask the guardian for the authorization from that same tab: they confirm it themselves from a single-use link, so the record shows that they did it and not your staff.`,
              msg`The age below which authorization is required is set per academy in the configuration, because it changes from country to country.`,
            ]}
          />
          <Note
            title={msg`Both switches start off`}
            msg={msg`Requiring the authorization before posting counts every student with no date of birth on file as a minor. Import files do not carry that date, so switching it on before completing the records would silence most of your students without protecting anyone. Complete the dates first.`}
          />
          <GoTo to="/academy/people" label={msg`Open People directory`} />
        </>
      ),
    },
    {
      id: "academico",
      title: msg`Programs, courses and cohorts`,
      summary: msg`A course is a content template. A cohort is a scheduled edition of a course: modality, campus, teachers, dates, capacity and price. Students enroll in cohorts.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Programs group several courses into a diploma or career path.`,
              msg`Courses are built in Authoring with units, lessons and interactive blocks; per-course settings choose which module kinds are enabled.`,
              msg`Cohorts define modality (in-person, live online, hybrid, self-paced), schedule slots, capacity and enrollment rule (open, approval or waitlist).`,
              msg`Closing a cohort seals final grades and issues verifiable certificates.`,
            ]}
          />
          <GoTo to="/academy/cohorts" label={msg`Open Cohorts`} />
        </>
      ),
    },
    // B6 · LEVELS: the decision that needs explaining isn't "how a path is
    // created", but that WHAT A LEVEL IS is configuration, not a constant.
    {
      id: "rutas",
      title: msg`Learning paths and levels`,
      summary: msg`A path turns a programme into levels the student unlocks one after another. What counts as a "level" is a setting, not a rule: by default it's one course of a programme, but it can be a whole programme, a unit, or levels you define yourself.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Pick the source of the levels: courses of a programme (the default), a chain of programmes, units of a course, or fully manual.`,
              msg`Reconciling regenerates the levels from that source. It never overwrites a title or rule you edited by hand, and never deletes a level.`,
              msg`What unlocks a level uses the same rule tree as your pass rules: "all" for a hard prerequisite, "any" to allow a placement test as an alternative, plus a payment condition if you sell level by level.`,
              msg`Levels start as a draft. Nothing changes for any student until you publish the path.`,
              msg`Completing a level can issue a certificate for that level, grant XP or a badge, and offer the next level.`,
            ]}
          />
          <GoTo to="/academy/programs" label={msg`Open Programs`} />
        </>
      ),
    },
    {
      id: "features",
      title: msg`Feature catalog and toggles`,
      summary: msg`Everything the platform can do is listed in the feature catalog — nothing is hidden. Each feature describes itself and can be turned on or off per scope.`,
      body: () => (
        <>
          <P
            msg={msg`Feature flags resolve in cascade: platform default → organization override → course override. Platform admins manage global flags; org admins can override features for their academy in the organization Settings tab.`}
          />
          <Note
            title={msg`Academy catalog over the API`}
            msg={msg`With «Academy catalog over the API» on, the list of courses your academy offers is requested from the server instead of being copied to every device. Drafts and deleted courses stay out of students' hands, and each student keeps offline only the course they are enrolled in.`}
          />
          <GoTo to="/admin/features" label={msg`Open the feature catalog`} />
        </>
      ),
    },
    {
      id: "trazabilidad",
      title: msg`Activity, undo and trash`,
      summary: msg`Every relevant change is recorded in the activity feed. Deleted items go to the trash, where they can be restored before the purge window expires.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Review who changed what in Activity.`,
              msg`Restore deleted items from the Trash while the retention window lasts.`,
              msg`Press Ctrl+Z (Cmd+Z) to undo your latest action.`,
            ]}
          />
          <GoTo to="/activity" label={msg`Open Activity`} />
        </>
      ),
    },
  ],
};
